import type { OwnerContext } from './actividad';

export type MedioPago = 'debito' | 'transferencia' | 'efectivo';

/** Orden fijado por el usuario (spec, decisión 8): Débito, Transferencia, Efectivo. */
export const MEDIOS_DE_PAGO: ReadonlyArray<{ readonly value: MedioPago; readonly label: string }> = [
  { value: 'debito', label: 'Débito' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'efectivo', label: 'Efectivo' },
];

/** Entidad dueña del dinero. `global` no tiene dinero propio. */
export interface EntidadDinero extends OwnerContext {
  readonly tipo: 'lumisial' | 'diocesis' | 'provincia';
}

export type TipoArchivoComprobante = 'imagen' | 'pdf';

export interface Comprobante {
  readonly id: string;
  readonly lumisial_id: string | null;
  readonly diocesis_id: string | null;
  readonly provincia_eclesiastica_id: string | null;
  readonly tipo_archivo: TipoArchivoComprobante;
  readonly rutas: readonly string[];
  readonly nombre_original: string | null;
  readonly tamano_bytes: number;
  readonly subido_por_usuario_id: string;
  readonly created_at: string;
}

/** Lo mínimo para mostrar y abrir un comprobante desde una fila (se trae embebido en el listado). */
export type ComprobanteResumen = Pick<Comprobante, 'id' | 'tipo_archivo' | 'rutas' | 'nombre_original'>;

export interface ComprobanteReciente extends Comprobante {
  readonly movimientos: ReadonlyArray<{
    readonly fecha: string;
    readonly monto: number;
    readonly descripcion: string | null;
  }>;
}

export type Respaldo = 'pago' | 'reembolso';

/** Fila de la vista `comprobantes_faltantes`. */
export interface ComprobanteFaltante {
  readonly movimiento_id: string;
  readonly respaldo: Respaldo;
  readonly lumisial_id: string | null;
  readonly diocesis_id: string | null;
  readonly provincia_eclesiastica_id: string | null;
  readonly fecha: string;
  readonly tipo: 'ingreso' | 'egreso';
  readonly monto: number;
  readonly descripcion: string | null;
  readonly pendiente_desde: string;
  readonly objecion_motivo: string | null;
}

export interface HistorialComprobante {
  readonly id: string;
  readonly movimiento_id: string;
  readonly comprobante_id: string;
  readonly respaldo: Respaldo;
  readonly motivo: 'reemplazado' | 'objetado';
  readonly objecion_motivo: string | null;
  readonly created_at: string;
}

/** Lo que un formulario agrega al movimiento que registra. */
export interface RespaldoMovimiento {
  readonly medio_pago: MedioPago | null;
  readonly folio_recibo: number | null;
  readonly comprobante_id: string | null;
  readonly comprobante_pendiente_desde: string | null;
}

export interface DatosReembolso extends RespaldoMovimiento {
  readonly fecha: string;
}

export interface ObjecionComprobante {
  readonly movimientoId: string;
  readonly respaldo: Respaldo;
  readonly motivo: string;
}

/** Página de foto procesada en el dispositivo, lista para subir. */
export interface PaginaComprobante {
  readonly id: string;
  readonly blob: Blob;
  readonly vistaPrevia: string;
}

export type SeleccionComprobante =
  | { readonly tipo: 'ninguno' }
  | { readonly tipo: 'imagenes'; readonly paginas: readonly PaginaComprobante[] }
  | { readonly tipo: 'pdf'; readonly archivo: File; readonly protegido: boolean }
  | { readonly tipo: 'existente'; readonly comprobante: ComprobanteReciente }
  | { readonly tipo: 'despues' };

export type EstadoComprobante =
  | { readonly tipo: 'adjunto'; readonly comprobante: ComprobanteResumen }
  | { readonly tipo: 'pendiente'; readonly pendienteDesde: string; readonly objecionMotivo: string | null }
  | { readonly tipo: 'vencido'; readonly pendienteDesde: string; readonly objecionMotivo: string | null }
  | { readonly tipo: 'marcha_blanca' }
  | { readonly tipo: 'exento'; readonly motivo: 'donacion' | 'pago_automatico' | 'traspaso' | 'envio' | 'apertura' };

export const MOTIVOS_OBJECION = ['Ilegible', 'El monto no coincide', 'No es un comprobante', 'Otro'] as const;
